import { useEffect, useState } from "react";
import './ProductDetails.scss'
import { BsHandbagFill } from "react-icons/bs";
// import star from '../Components/images/star.svg'
import { FaRegHeart } from "react-icons/fa6";
import { CiDeliveryTruck } from "react-icons/ci";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useProfile } from "../../Contexts/ProfileContext";
import { getDeliveryEstimate } from "../../api/delivery";
import { useToast } from "../../Contexts/ToastContext";

const PINCODE_STORAGE_KEY = 'savedPincode';
/** Used when `savedPincode` is missing or invalid in localStorage (6-digit IN pincode) */
const DEFAULT_PINCODE = '560006';

function readSavedPincodeFromStorage() {
    try {
        const s = localStorage.getItem(PINCODE_STORAGE_KEY);
        if (s && /^\d{6}$/.test(String(s).trim())) return String(s).trim();
    } catch (_) { /* ignore */ }
    return null;
}

function persistSavedPincode(value) {
    const v = (value ?? '').toString().trim();
    if (!/^\d{6}$/.test(v)) return;
    try {
        localStorage.setItem(PINCODE_STORAGE_KEY, v);
    } catch (_) { /* ignore quota / private mode */ }
}

export function ProductDetails({ product_id, initialProduct }) {
    const [productDetails, setProductDetails] = useState(initialProduct || {});
    const [selectedSize, setSelectedSize] = useState(null);
    const [loading, setLoading] = useState(!initialProduct);
    const [pincode, setPincode] = useState(() => readSavedPincodeFromStorage() || DEFAULT_PINCODE);
    const [pincodeError, setPincodeError] = useState('');
    const [deliveryDate, setDeliveryDate] = useState(null);
    const [deliveryLoading, setDeliveryLoading] = useState(true);
    const [cartLoading, setCartLoading] = useState(false);
    const [wishlistLoading, setWishlistLoading] = useState(false);
    const navigate = useNavigate();
    const { fetchProfile } = useProfile();
    const { showToast } = useToast();

    // Keep `savedPincode` in sync whenever the field holds a valid 6-digit pincode
    useEffect(() => {
        persistSavedPincode(pincode);
    }, [pincode]);

    // Sync when parent passes new initialProduct (e.g. after fetch)
    useEffect(() => {
        if (initialProduct && Object.keys(initialProduct).length) {
            setProductDetails(initialProduct);
            setLoading(false);
        }
    }, [initialProduct]);

    // Fetch product specifications when no initialProduct (e.g. direct nav to page)
    async function fetchProductSpecifications() {
        if (!product_id) return;
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            const response = await axios.post(
                `/products/specifications/${product_id}`,
                {},
                config
            );

            let data = response.data;
            setProductDetails({ ...data });
        }
        catch (error) {
            console.error("Failed to fetch product specifications:", error);
        }
        finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (product_id && !initialProduct) {
            fetchProductSpecifications();
        }
    }, [product_id, initialProduct]);

    // Load pincode on mount: localStorage `savedPincode` first, else DEFAULT_PINCODE. Then delivery estimate API.
    useEffect(() => {
        let cancelled = false;
        async function load() {
            setDeliveryLoading(true);
            try {
                let pincodeToUse = readSavedPincodeFromStorage();
                if (!pincodeToUse) {
                    pincodeToUse = DEFAULT_PINCODE;
                }
                if (cancelled) return;
                setPincode(pincodeToUse);
                const result = await getDeliveryEstimate(pincodeToUse);
                if (cancelled) return;
                if (result.serviceable && result.message) {
                    setPincodeError('');
                    setDeliveryDate(result.message);
                } else {
                    setDeliveryDate(null);
                }
            } finally {
                if (!cancelled) setDeliveryLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, []);

    async function handlePincodeCheck() {
        const val = pincode.trim();
        setPincodeError('');
        setDeliveryDate(null);
        if (val.length !== 6) {
            setPincodeError('Please enter a 6-digit pincode.');
            return;
        }
        setDeliveryLoading(true);
        try {
            const result = await getDeliveryEstimate(val);
            if (result.serviceable && result.message) {
                setPincodeError('');
                setDeliveryDate(result.message);
            } else {
                setPincodeError(result.message || "We don't deliver to this pincode yet.");
            }
        } finally {
            setDeliveryLoading(false);
        }
    }

    // Handle size selection
    function selectSize(sizeIndex) {
        const sizeKeys = productDetails?.size ? Object.keys(productDetails.size) : [];
        const selectedSizeKey = sizeKeys[sizeIndex];
        const stock = productDetails?.size?.[selectedSizeKey] || 0;
        
        // Only allow selection if stock is available
        if (stock > 0) {
            setSelectedSize(sizeIndex);
        }
    }

    function getSelectedSizeKey() {
        const sizeKeys = productDetails?.size ? Object.keys(productDetails.size) : [];
        if (selectedSize === null || sizeKeys[selectedSize] == null) return null;
        return sizeKeys[selectedSize];
    }

    // Handle add to cart
    async function addToCart() {
        if (cartLoading) return;

        try {
            const isGuest = localStorage.getItem('guestUser') === 'true';
            if (!isGuest && !localStorage.getItem("token")) {
                showToast({
                    message: 'Please login to add items to cart',
                    variant: 'info',
                    action: { label: 'Login', onClick: () => navigate('/auth/login') },
                });
                return;
            }

            if (selectedSize === null) {
                showToast({ message: 'Please select a size', variant: 'warning' });
                return;
            }

            const selectedSizeKey = getSelectedSizeKey();
            const stock = productDetails?.size?.[selectedSizeKey] || 0;

            if (stock === 0) {
                showToast({ message: 'Selected size is out of stock', variant: 'warning' });
                return;
            }

            setCartLoading(true);

            if (isGuest) {
                const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
                const existingIndex = guestCart.findIndex((c) => String(c.product_id) === String(product_id));
                if (existingIndex >= 0) {
                    guestCart[existingIndex].selectedQty = (guestCart[existingIndex].selectedQty || 1) + 1;
                } else {
                    const productSizes = productDetails?.size || { [selectedSizeKey]: 10 };
                    guestCart.push({
                        _id: `guest-cart-${Date.now()}-${product_id}`,
                        product_id,
                        urls: productDetails?.urls || [],
                        title: productDetails?.title || 'Product',
                        price: productDetails?.price,
                        brand: productDetails?.brand,
                        mrp: productDetails?.mrp,
                        selectedSize: selectedSizeKey,
                        selectedQty: 1,
                        size: productSizes,
                        availableSizes: Object.keys(productSizes).filter(s => productSizes[s] > 0) || [selectedSizeKey],
                        availableQuantities: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
                        maxStock: productSizes[selectedSizeKey] || 10
                    });
                }
                localStorage.setItem('guestCart', JSON.stringify(guestCart));
                window.dispatchEvent(new CustomEvent('guestCartUpdated'));
                window.dispatchEvent(new Event('cartBadgePulse'));
                showToast({
                    message: 'Added to bag',
                    action: { label: 'View cart', onClick: () => navigate('/cart') },
                });
                return;
            }

            const token = localStorage.getItem("token");
            const config = { headers: { Authorization: `Bearer ${token}` } };

            const pincodeToSend = (pincode && pincode.length === 6 && /^\d{6}$/.test(pincode)) ? pincode : '';
            await axios.post(
                `/cart/${product_id}`,
                { selectedSize: selectedSizeKey, selectedQty: 1, pincode: pincodeToSend },
                config
            );

            try {
                await fetchProfile();
            } catch (profileError) {
                console.error('Failed to refetch profile data:', profileError);
            }

            window.dispatchEvent(new Event('cartBadgePulse'));
            showToast({
                message: 'Added to bag',
                action: { label: 'View cart', onClick: () => navigate('/cart') },
            });
        }
        catch (error) {
            console.error("Failed to add to cart:", error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to add item to cart. Please try again.';
            showToast({ message: errorMessage, variant: 'error' });
        } finally {
            setCartLoading(false);
        }
    }

    async function addToWishlist() {
        if (wishlistLoading) return;

        try {
            const isGuest = localStorage.getItem('guestUser') === 'true';
            if (!isGuest && !localStorage.getItem("token")) {
                showToast({
                    message: 'Please login to add items to wishlist',
                    variant: 'info',
                    action: { label: 'Login', onClick: () => navigate('/auth/login') },
                });
                return;
            }

            if (selectedSize === null) {
                showToast({ message: 'Please select a size', variant: 'warning' });
                return;
            }

            const selectedSizeKey = getSelectedSizeKey();
            setWishlistLoading(true);

            if (isGuest) {
                const guestWishlist = JSON.parse(localStorage.getItem('guestWishlist') || '[]');
                const exists = guestWishlist.some((item) => String(item.product_id) === String(product_id));
                if (exists) {
                    showToast({
                        message: 'Already in wishlist',
                        action: { label: 'View wishlist', onClick: () => navigate('/wishlist') },
                    });
                    return;
                }

                guestWishlist.push({
                    _id: `guest-wish-${Date.now()}-${product_id}`,
                    product_id,
                    urls: productDetails?.urls || [],
                    title: productDetails?.title || 'Product',
                    price: productDetails?.price,
                    brand: productDetails?.brand,
                    mrp: productDetails?.mrp,
                    selectedSize: selectedSizeKey,
                });
                localStorage.setItem('guestWishlist', JSON.stringify(guestWishlist));
                showToast({
                    message: 'Added to wishlist',
                    action: { label: 'View wishlist', onClick: () => navigate('/wishlist') },
                });
                return;
            }

            const token = localStorage.getItem("token");
            const config = { headers: { Authorization: `Bearer ${token}` } };

            await axios.post(
                `/products/wish-list/${product_id}`,
                { selectedSize: selectedSizeKey },
                config
            );

            try {
                await fetchProfile();
            } catch (profileError) {
                console.error('Failed to refetch profile data:', profileError);
            }

            showToast({
                message: 'Added to wishlist',
                action: { label: 'View wishlist', onClick: () => navigate('/wishlist') },
            });
        } catch (error) {
            if (error.response?.status === 400) {
                showToast({
                    message: 'Already in wishlist',
                    action: { label: 'View wishlist', onClick: () => navigate('/wishlist') },
                });
            } else {
                console.error('Failed to add to wishlist:', error);
                showToast({ message: 'Could not add to wishlist. Try again.', variant: 'error' });
            }
        } finally {
            setWishlistLoading(false);
        }
    }

    return (
        <div className="product-details-mob">
            <div className="delivery-check-card">
                <header className="card-header">
                    <div className="card-title">
                        CHECK DELIVERY &amp; SERVICES
                    </div>
                </header>

                <div className="address-box pincode-input">
                    <input
                        type="tel"
                        placeholder="Enter a PIN code"
                        value={pincode}
                        maxLength={6}
                        inputMode="numeric"
                        onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setPincode(v);
                            setPincodeError('');
                            setDeliveryDate(null);
                        }}
                    />
                    <button
                        type="button"
                        className="address-button address-button-check"
                        onClick={handlePincodeCheck}
                        disabled={pincode.length !== 6 || deliveryLoading}
                    >
                        CHECK
                    </button>
                </div>

                <div className="delivery-result-mob" aria-live="polite">
                    {deliveryLoading && (
                        <div className="delivery-result-mob__loader" role="status" aria-busy="true">
                            <span className="delivery-spinner" aria-hidden />
                            <span className="delivery-result-mob__sr-only">Checking delivery</span>
                        </div>
                    )}
                    {!deliveryLoading && deliveryDate && (
                        <div className="delivery-date-mob">
                            <CiDeliveryTruck /> {deliveryDate}
                        </div>
                    )}
                    {!deliveryLoading && pincodeError && (
                        <div className="delivery-error-mob">{pincodeError}</div>
                    )}
                </div>
            </div>


            {/* <div className="price-info">
                <h1 class="title">BULLMER</h1>
                <h1 class="name">Tribal Printed Oversized Cotton T-shirt</h1>

                <div className="rating-container">
                    <div className="overall-rating">
                        <div>3.7</div>
                        <span className="myntraweb-sprite"></span>
                        <div class="index-separator">|</div>
                        <div>480 Ratings</div>
                    </div>
                </div>

                <div className="discount-container">
                    <div className="price">₹389</div>
                    <div className="mrp">MRP <s>1499</s></div>
                    <div className="discount">(74% OFF)</div>
                </div>

                <div className="vat-info">Inclusive of all taxes</div>

            </div> */}

            <div className="other-details">
                <div className="quantity-button-container">
                    {/* <div className="header">
                        <h4 class="size-buttons-select-size">
                            SELECT SIZE
                        </h4>
                    </div> */}

                    <div class="grid padding-md-left padding-md-top padding-md-right card-header">
                        <div class="col-2-3">
                            <h4 class="card-title">
                                <span class="select-size">
                                    Select Size
                                </span>
                            </h4>
                        </div>
                        {/* <div class="col-1-3">
                            <div class="ripple-container size-chart-button">
                                <button class="btn primary flat   " id="" fdprocessedid="u78uhc">
                                    SIZE CHART
                                </button>
                                <div class="ripple">
                                </div>
                            </div>
                        </div> */}
                    </div>

                    <div className="buttons">
                        {
                            productDetails?.size &&
                            Object.keys(productDetails.size).map((sizeKey, sizeIndex) => {
                                const stock = productDetails.size[sizeKey] || 0;
                                const isSelected = selectedSize === sizeIndex;
                                const isDisabled = stock === 0;
                                const isLowStock = stock > 0 && stock < 10;

                                return (
                                    <div
                                        key={sizeIndex}
                                        className={
                                            isSelected
                                                ? 'button size-button-selected'
                                                : isDisabled
                                                    ? 'button disabled-button'
                                                    : 'button'
                                        }
                                        onClick={() => !isDisabled && selectSize(sizeIndex)}
                                        style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                                    >
                                        <span className="size-label">{String(sizeKey).toUpperCase()}</span>
                                        {isLowStock && !isDisabled && (
                                            <span className="stock-indicator">{stock} left</span>
                                        )}
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>

                <div className="action-container">
                    <button
                        type="button"
                        className={`add-to-list${cartLoading ? ' add-to-list--loading' : ''}`}
                        onClick={addToCart}
                        disabled={cartLoading || wishlistLoading}
                        aria-busy={cartLoading}
                    >
                        <span className="add-to-list__icon" aria-hidden>
                            {cartLoading ? <span className="action-btn-spinner" /> : <BsHandbagFill width={150} height={150} />}
                        </span>
                        {cartLoading ? 'ADDING...' : 'ADD TO BAG'}
                    </button>
                    <button
                        type="button"
                        className={`wishlist${wishlistLoading ? ' wishlist--loading' : ''}`}
                        onClick={addToWishlist}
                        disabled={cartLoading || wishlistLoading}
                        aria-busy={wishlistLoading}
                    >
                        <span className="wishlist__icon" aria-hidden>
                            {wishlistLoading ? <span className="action-btn-spinner action-btn-spinner--wishlist" /> : <FaRegHeart />}
                        </span>
                        <span>{wishlistLoading ? 'ADDING...' : 'WISHLIST'}</span>
                    </button>
                </div>



            </div>

        </div>
    )
}